import { detectGpc, hasConsentFor } from './consent';

import { ensureGtagBootstrap } from './gtag-core';

import { Logger } from './logger';



const PLACEHOLDER_ID_PATTERNS = [

  /^aw-x+$/i,

  /^your-google-ads-id$/i,

];

const GOOGLE_ADS_ID_PATTERN = /^AW-\d+$/;
const GOOGLE_ADS_CONVERSION_LABEL_PATTERN = /^[A-Za-z0-9_-]+$/;
const GOOGLE_ADS_CONVERSION_LABEL_PLACEHOLDER_PATTERNS = [/^your-.*-label$/i, /^placeholder$/i];
const DEFAULT_GOOGLE_ADS_ID = 'AW-18423276605';

// Paid subscription conversion action.
const GOOGLE_ADS_CONVERSION_LABEL = 'S8OPCJneo-0cEL3I89BE';

let loaded = false;


const trimString = (value: unknown): string | undefined => {

  if (typeof value !== 'string') {

    return undefined;

  }

  const trimmed = value.trim();

  return trimmed ? trimmed : undefined;

};



export const resolveGoogleAdsId = (): string | undefined => {
  const trimmed = trimString(process.env.NEXT_PUBLIC_GOOGLE_ADS_ID) ?? DEFAULT_GOOGLE_ADS_ID;

  if (!trimmed) {

    return undefined;

  }

  if (PLACEHOLDER_ID_PATTERNS.some((pattern) => pattern.test(trimmed))) {

    return undefined;

  }

  if (!GOOGLE_ADS_ID_PATTERN.test(trimmed)) {

    return undefined;

  }

  return trimmed;
};

export const resolveGoogleAdsFreeSampleConversionLabel = (): string | undefined => {
  const label = trimString(process.env.NEXT_PUBLIC_GOOGLE_ADS_FREE_SAMPLE_CONVERSION_LABEL);
  if (
    !label
    || GOOGLE_ADS_CONVERSION_LABEL_PLACEHOLDER_PATTERNS.some((pattern) => pattern.test(label))
    || !GOOGLE_ADS_CONVERSION_LABEL_PATTERN.test(label)
  ) {
    return undefined;
  }
  return label;
};



export const googleAdsAllowed = (): boolean => {

  if (typeof window === 'undefined') {

    return false;

  }

  return hasConsentFor('marketing') && !detectGpc();

};



export const loadGoogleAdsTag = (): boolean => {
  if (typeof window === 'undefined' || loaded) {

    return loaded;
  }

  if (!googleAdsAllowed()) {

    return false;
  }

  const adsId = resolveGoogleAdsId();

  if (!adsId) {

    return false;
  }



  try {

    ensureGtagBootstrap(adsId);

    if (!window.gtag) {

      return false;
    }

    window.gtag('consent', 'update', {

      ad_storage: 'granted',

      ad_user_data: 'granted',

      ad_personalization: 'granted',

    });

    window.gtag('config', adsId);
    loaded = true;

    return true;
  } catch (error) {
    Logger.warn('Google Ads failed to load', {

      error: error instanceof Error ? error.message : String(error),

    });

    return false;
  }
};


type GoogleAdsConversionParams = {
  transactionId: string;
  value?: number;
  currency?: string;
};

const trackGoogleAdsConversionWithLabel = (
  conversionLabel: string,
  { transactionId, value, currency }: GoogleAdsConversionParams,
): boolean => {
  if (typeof window === 'undefined' || !loaded || !window.gtag) {
    return false;
  }

  if (!googleAdsAllowed()) {
    return false;
  }

  const adsId = resolveGoogleAdsId();
  if (!adsId) {
    return false;
  }

  try {
    window.gtag('event', 'conversion', {
      send_to: `${adsId}/${conversionLabel}`,
      transaction_id: transactionId,
      ...(value !== undefined ? { value, currency: currency ?? 'usd' } : {}),
    });
    return true;
  } catch (error) {
    Logger.warn('Google Ads failed to track conversion', {

      error: error instanceof Error ? error.message : String(error),

    });

    return false;
  }
};

export const trackGoogleAdsConversion = (params: GoogleAdsConversionParams): boolean =>
  trackGoogleAdsConversionWithLabel(GOOGLE_ADS_CONVERSION_LABEL, params);

export const trackGoogleAdsFreeSampleConversion = ({ transactionId }: {
  transactionId: string;
}): boolean => {
  const conversionLabel = resolveGoogleAdsFreeSampleConversionLabel();
  if (!conversionLabel) {
    return false;
  }
  return trackGoogleAdsConversionWithLabel(conversionLabel, { transactionId });
};


export const __resetGoogleAdsForTests = (): void => {

  loaded = false;

};
